import React, { Dispatch, useCallback, useEffect, useRef } from 'react'
import { Box, Button, Text } from '@chakra-ui/react'
import { ActionTypesMap, AnyActionType, KeybindAction } from '~core/forge.type'
import useDispatch from '~editor/hooks/useDispatch'
import { IProps } from '~editor/ActionEditor/ActionsComponents/KeybindActionComponent'
import { RecordingAction } from '~editor/models/editor'
import { isMetaKey } from '~core/utils/keyboard'

interface IRecordKeybindComponent extends IProps {
  recording: boolean
  recordingDispatch: Dispatch<RecordingAction>
}

export const RecordKeybindComponent = ({
  action,
  actions,
  activeActionGesture,
  recording,
  recordingDispatch,
}: IRecordKeybindComponent) => {
  const oldTimeStamp = useRef(0)
  const hiddenInputRef = useRef<HTMLInputElement>(null)
  const dispatch = useDispatch()

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (ActionTypesMap[action.type] !== KeybindAction) return
      e.preventDefault()
      const _action = action as KeybindAction

      const newKeys = [...(_action.keys || [])]

      const { key, code, timeStamp, repeat } = e

      if (repeat) {
        return
      }

      if (!code && !key) {
        return
      }

      if (code === 'AltRight') {
        const [tail] = _action.keys.slice(-1)

        if (tail === 'ControlLeft' && timeStamp - oldTimeStamp.current < 150) {
          newKeys.pop()
        }
      }

      oldTimeStamp.current = timeStamp

      if (isMetaKey(code) || !newKeys.some((k) => !isMetaKey(k))) {
        newKeys.push(code || key)
      } else {
        newKeys.splice(
          newKeys.findIndex((k) => !isMetaKey(k)),
          1,
          code,
        )
      }

      dispatch.nodes.updateActions({
        actionGestureType: activeActionGesture,
        actions: actions.map((_a: AnyActionType) => {
          const _action = { ..._a } as KeybindAction

          if (action.id === _action.id) {
            _action.keys = Array.from(new Set(newKeys)).sort((a, b) => {
              const isMetaKeyA = isMetaKey(a)
              const isMetaKeyB = isMetaKey(b)

              if (isMetaKeyA && !isMetaKeyB) return -1
              if (isMetaKeyB && !isMetaKeyA) return 1
              return 0
            })
          }
          return _action
        }),
      })
    },
    [action, actions],
  )

  useEffect(() => {
    if (recording) {
      window.addEventListener('keydown', onKeyDown, {
        capture: true,
      })
      return () =>
        window.removeEventListener('keydown', onKeyDown, {
          capture: true,
        })
    }
  }, [recording, action, actions])

  return (
    <Box
      display="flex"
      justifyContent={'end'}
      flex={1}
      alignItems="center"
      marginLeft={'2'}
    >
      {recording && !action?.keys?.length && (
        <Text>Recording keystrokes...</Text>
      )}

      <input
        ref={hiddenInputRef}
        onKeyUp={() => recordingDispatch(RecordingAction.STOP_RECORDING)}
        onFocus={() => recordingDispatch(RecordingAction.START_RECORDING)}
        onBlur={() => recordingDispatch(RecordingAction.STOP_RECORDING)}
        onClick={() => recordingDispatch(RecordingAction.STOP_RECORDING)}
        style={{
          cursor: 'inherit',
          opacity: 0,
          zIndex: 2,
          position: 'absolute',
          width: recording ? '100%' : 0,
          height: recording ? '100%' : 0,
        }}
      />
      <Button
        variant={recording ? 'warningGhost' : 'warning'}
        fontSize="inherit"
        height="auto"
        paddingLeft="8px"
        paddingRight="8px"
        border="3px solid gameglass.50"
        width="55px"
        minWidth="55px"
        className="recordButton" // Needed for onboarding
        position="relative"
        onClick={() => {
          if (!recording && action.keys?.length !== 0) {
            dispatch.nodes.updateActions({
              actionGestureType: activeActionGesture,
              actions: actions.map((_a: AnyActionType) => {
                const _action = { ..._a } as KeybindAction

                if (action.id === _action.id) {
                  _action.keys = []
                }
                return _action
              }),
            })
            hiddenInputRef.current?.focus()
          } else {
            hiddenInputRef.current?.focus()
          }
        }}
      >
        {' '}
        {recording ? 'Stop' : 'Record'}
      </Button>
    </Box>
  )
}
